import { useEffect, useMemo, useRef, useState } from "react";
import { getCurrentSession } from "../../services/auth";

const API_URL = (
  process.env.REACT_APP_API_URL || "https://zedro-backend.onrender.com"
).replace(/\/$/, "");

const PAGE_SIZE = 25;

function authHeaders(includeJson = false) {
  const session = getCurrentSession();
  const headers = {};
  if (includeJson) headers["Content-Type"] = "application/json";
  if (session?.token) headers.Authorization = `Bearer ${session.token}`;
  return headers;
}

function normalizeIds(values = []) {
  return [...new Set(values.map((value) => String(value)))].sort();
}

export default function MembershipProductInclusionManager({ productId }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [baseline, setBaseline] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: PAGE_SIZE,
    total: 0,
    total_pages: 1,
  });
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let alive = true;
    const requestId = ++requestIdRef.current;

    async function loadPage() {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(PAGE_SIZE),
          status,
        });
        if (search) params.set("search", search);

        const response = await fetch(
          `${API_URL}/api/admin/membership-product-inclusions/${productId}?${params.toString()}`,
          { headers: authHeaders() }
        );
        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data?.ok) {
          throw new Error(
            data?.message ||
              data?.error ||
              "No fue posible cargar los productos de la membresía."
          );
        }

        if (!alive || requestId !== requestIdRef.current) return;

        setItems(Array.isArray(data.products) ? data.products : []);
        setPagination(
          data.pagination || {
            page,
            page_size: PAGE_SIZE,
            total: 0,
            total_pages: 1,
          }
        );

        if (!initialized) {
          const snapshot = normalizeIds(data.included_product_ids || []);
          setSelected(snapshot);
          setBaseline(snapshot);
          setInitialized(true);
        }
      } catch (err) {
        console.error("[ADMIN_MEMBERSHIP_INCLUSIONS_LOAD]", err);
        if (alive && requestId === requestIdRef.current) {
          setError(
            err.message || "No fue posible cargar los productos de la membresía."
          );
        }
      } finally {
        if (alive && requestId === requestIdRef.current) setLoading(false);
      }
    }

    loadPage();
    return () => {
      alive = false;
    };
  }, [initialized, page, productId, search, status]);

  const normalizedSelected = useMemo(() => normalizeIds(selected), [selected]);
  const normalizedBaseline = useMemo(() => normalizeIds(baseline), [baseline]);
  const dirty =
    JSON.stringify(normalizedSelected) !== JSON.stringify(normalizedBaseline);
  const selectedSet = useMemo(
    () => new Set(normalizedSelected),
    [normalizedSelected]
  );

  function toggle(productIdValue) {
    setNotice("");
    setError("");
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(productIdValue)) next.delete(productIdValue);
      else next.add(productIdValue);
      return [...next];
    });
  }

  function discard() {
    setSelected([...baseline]);
    setNotice("Cambios descartados.");
    setError("");
  }

  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    setError("");
    setNotice("");

    try {
      const snapshot = normalizeIds(selected);
      const response = await fetch(
        `${API_URL}/api/admin/membership-product-inclusions/${productId}`,
        {
          method: "PUT",
          headers: authHeaders(true),
          body: JSON.stringify({ product_ids: snapshot }),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            "No fue posible guardar los productos de la membresía."
        );
      }

      const saved = normalizeIds(data.saved?.product_ids || snapshot);
      setSelected(saved);
      setBaseline(saved);
      setNotice("Productos incluidos guardados correctamente.");
    } catch (err) {
      console.error("[ADMIN_MEMBERSHIP_INCLUSIONS_SAVE]", err);
      setError(
        err.message || "No fue posible guardar los productos de la membresía."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section style={sectionStyle}>
      <div style={headerStyle}>
        <div>
          <div style={eyebrowStyle}>Cobertura de la membresía</div>
          <h3 style={titleStyle}>Productos incluidos</h3>
          <p style={descriptionStyle}>
            Selecciona productos completos que la membresía habilitará mientras el
            derecho del miembro esté vigente. Cada producto conserva una sola
            identidad y puede venderse también de forma individual.
          </p>
        </div>
        <div style={summaryStyle}>
          <strong>{normalizedSelected.length}</strong>
          <span>
            {normalizedSelected.length === 1
              ? "producto incluido"
              : "productos incluidos"}
          </span>
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}
      {notice && <div style={noticeStyle}>{notice}</div>}

      <div style={toolbarStyle}>
        <input
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Buscar producto por nombre..."
          style={searchStyle}
        />
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
          style={selectStyle}
        >
          <option value="all">Todos los estados</option>
          <option value="published">Publicados</option>
          <option value="draft">Borradores</option>
          <option value="archived">Archivados</option>
        </select>
        {dirty && <span style={pendingStyle}>Cambios sin guardar</span>}
      </div>

      {loading && !initialized ? (
        <div style={emptyStyle}>Cargando catálogo de productos...</div>
      ) : items.length === 0 ? (
        <div style={emptyStyle}>
          {search || status !== "all"
            ? "No hay productos que coincidan con estos filtros."
            : "Todavía no hay productos individuales disponibles."}
        </div>
      ) : (
        <div style={itemListStyle} aria-busy={loading}>
          {items.map((item) => {
            const checked = selectedSet.has(item.id);
            return (
              <label key={item.id} style={itemStyle}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(item.id)}
                />
                <div style={{ flex: 1 }}>
                  <div style={itemTitleStyle}>{item.name}</div>
                  <div style={itemMetaStyle}>
                    <span>{item.product_type}</span>
                    <span>·</span>
                    <span>{item.status}</span>
                    {item.is_active === false && (
                      <>
                        <span>·</span>
                        <span>inactivo</span>
                      </>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      <div style={paginationStyle}>
        <button
          type="button"
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          disabled={pagination.page <= 1 || loading}
          style={secondaryButtonStyle}
        >
          Anterior
        </button>
        <span style={pageTextStyle}>
          Página {pagination.page} de {Math.max(1, pagination.total_pages)} · {pagination.total} productos
        </span>
        <button
          type="button"
          onClick={() =>
            setPage((current) =>
              Math.min(Math.max(1, pagination.total_pages), current + 1)
            )
          }
          disabled={pagination.page >= pagination.total_pages || loading}
          style={secondaryButtonStyle}
        >
          Siguiente
        </button>
      </div>

      <div style={actionsStyle}>
        <button
          type="button"
          onClick={discard}
          disabled={!dirty || saving}
          style={secondaryButtonStyle}
        >
          Descartar cambios
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          style={primaryButtonStyle}
        >
          {saving ? "Guardando..." : "Guardar productos incluidos"}
        </button>
      </div>
    </section>
  );
}

const sectionStyle = {
  border: "1px solid #e6ebf1",
  borderRadius: 14,
  padding: 18,
  background: "#fff",
  boxShadow: "0 10px 30px rgba(0,0,0,.05)",
  marginTop: 20,
  marginBottom: 20,
};
const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 20,
  flexWrap: "wrap",
  alignItems: "flex-start",
};
const eyebrowStyle = {
  color: "#CCA352",
  fontSize: 12,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: ".08em",
};
const titleStyle = { margin: "6px 0 8px", color: "#0a2230" };
const descriptionStyle = {
  margin: 0,
  color: "#617181",
  lineHeight: 1.55,
  maxWidth: 650,
};
const summaryStyle = {
  minWidth: 145,
  border: "1px solid #dfe7ee",
  borderRadius: 12,
  padding: "10px 14px",
  display: "flex",
  flexDirection: "column",
  color: "#264763",
};
const toolbarStyle = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  flexWrap: "wrap",
  margin: "18px 0 14px",
};
const searchStyle = {
  flex: "1 1 280px",
  minWidth: 220,
  border: "1px solid #d7e0e8",
  borderRadius: 9,
  padding: "10px 12px",
  font: "inherit",
};
const selectStyle = {
  border: "1px solid #d7e0e8",
  borderRadius: 9,
  padding: "10px 12px",
  background: "#fff",
  font: "inherit",
};
const pendingStyle = {
  color: "#8a641d",
  background: "#fff7df",
  border: "1px solid #ead7a1",
  borderRadius: 999,
  padding: "6px 10px",
  fontSize: 12,
  fontWeight: 700,
};
const itemListStyle = {
  display: "grid",
  gap: 8,
  opacity: 1,
};
const itemStyle = {
  display: "flex",
  gap: 11,
  alignItems: "flex-start",
  border: "1px solid #e5ebf0",
  borderRadius: 10,
  padding: "11px 12px",
  cursor: "pointer",
};
const itemTitleStyle = { color: "#263847", fontWeight: 700 };
const itemMetaStyle = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  marginTop: 4,
  color: "#748290",
  fontSize: 12,
  textTransform: "uppercase",
};
const emptyStyle = {
  border: "1px dashed #d9e1e7",
  borderRadius: 10,
  padding: 18,
  color: "#73808c",
  textAlign: "center",
};
const paginationStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 14,
};
const pageTextStyle = { color: "#687785", fontSize: 13 };
const actionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  flexWrap: "wrap",
  borderTop: "1px solid #edf1f4",
  paddingTop: 16,
  marginTop: 16,
};
const primaryButtonStyle = {
  border: 0,
  borderRadius: 9,
  background: "#264763",
  color: "#fff",
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};
const secondaryButtonStyle = {
  border: "1px solid #d8e0e7",
  borderRadius: 9,
  background: "#fff",
  color: "#314758",
  padding: "9px 12px",
  fontWeight: 700,
  cursor: "pointer",
};
const errorStyle = {
  marginTop: 12,
  padding: "10px 12px",
  borderRadius: 9,
  background: "#fff1f1",
  color: "#9a3030",
};
const noticeStyle = {
  marginTop: 12,
  padding: "10px 12px",
  borderRadius: 9,
  background: "#eef8ef",
  color: "#2f6e39",
};
